import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Container,
  IconButton,
  InputAdornment,
  Tabs,
  Tab
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Email,
  Lock,
  VpnKey
} from '@mui/icons-material';
import { AdminAuthService } from '../services/firebase';
import { useAppContext } from '../contexts/AppContext';
import { getFirestore, collection, query, where, getDocs, updateDoc, doc, addDoc } from 'firebase/firestore';
import { updatePassword } from 'firebase/auth';
import { auth } from '../config/firebase';
import app from '../config/firebase';

interface LoginProps {
  onLoginSuccess?: () => void;
}

const Login: React.FC<LoginProps> = ({ onLoginSuccess }) => {
  const navigate = useNavigate();
  const { refreshAuth } = useAppContext();
  const db = getFirestore(app);
  
  const [loginMode, setLoginMode] = useState<'password' | 'firstTime'>('password');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [pendingAdminData, setPendingAdminData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [newPasswordError, setNewPasswordError] = useState('');

  // Clear errors when user types
  useEffect(() => {
    if (error) setError('');
    if (emailError) setEmailError('');
  }, [email]);

  useEffect(() => {
    if (error) setError('');
    if (passwordError) setPasswordError('');
  }, [password]);

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateForm = (): boolean => {
    let isValid = true;
    setEmailError('');
    setPasswordError('');

    if (!email) {
      setEmailError('Email is required');
      isValid = false;
    } else if (!validateEmail(email)) {
      setEmailError('Please enter a valid email address');
      isValid = false;
    }

    if (!password) {
      setPasswordError('Password is required');
      isValid = false;
    } else if (password.length < 6) {
      setPasswordError('Password must be at least 6 characters');
      isValid = false;
    }

    return isValid;
  };

  const getErrorMessage = (error: any): string => {
    const code = error?.code || '';
    
    switch (code) {
      case 'auth/user-not-found':
        return 'No admin account found with this email address.';
      case 'auth/wrong-password':
        return 'Incorrect password. Please try again.';
      case 'auth/invalid-email':
        return 'Please enter a valid email address.';
      case 'auth/user-disabled':
        return 'This account has been disabled. Please contact support.';
      case 'auth/too-many-requests':
        return 'Too many failed attempts. Please try again later.';
      case 'auth/network-request-failed':
        return 'Network error. Please check your internet connection.';
      default:
        return error?.message || 'Failed to login. Please check your credentials.';
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (loginMode === 'password') {
      if (!validateForm()) {
        return;
      }
    } else {
      // Validate first-time login
      if (!email) {
        setEmailError('Email is required');
        return;
      }
      if (!password) {
        setPasswordError('Temporary password is required');
        return;
      }
    }

    setLoading(true);
    setError('');

    try {
      if (loginMode === 'firstTime') {
        // First-time login with temporary password
        try {
          // STEP 1: First authenticate with Firebase using the temporary password
          console.log('🔐 Attempting Firebase authentication with temporary password...');
          
          // Set a flag to indicate we're in password setup mode
          sessionStorage.setItem('passwordSetupMode', 'true');
          
          const userCredential = await AdminAuthService.login(email, password);
          console.log('✅ Firebase authentication successful');
          
          // IMPORTANT: Don't navigate yet - we need to show password change form
          // The passwordSetupMode flag will prevent automatic redirect

          // STEP 2: Now that we're authenticated, check adminUsers collection
          const adminsRef = collection(db, 'adminUsers');
          const q = query(adminsRef, where('email', '==', email.trim()));
          const snapshot = await getDocs(q);
          
          if (snapshot.empty) {
            throw new Error('No admin account found with this email address. Please contact your administrator.');
          }
          
          const adminDoc = snapshot.docs[0];
          const adminData = adminDoc.data();
          
          console.log('First-time login - Admin data:', {
            email: adminData.email,
            passwordChanged: adminData.passwordChanged,
            status: adminData.status
          });
          
          // Check if password already changed (user should use regular login)
          if (adminData.passwordChanged === true) {
            // Log them out since they're trying to use first-time login after already setting password
            await AdminAuthService.logout();
            throw new Error('This account has already been activated. Please use regular password login.');
          }
          
          // Show password change form
          console.log('📝 Showing password change form...');
          console.log('📝 Pending admin data:', { docId: adminDoc.id, email: adminData.email });
          setPendingAdminData({ docId: adminDoc.id, ...adminData });
          setShowPasswordChange(true);
          console.log('✅ showPasswordChange set to TRUE');
          console.log('✅ Password change form should now be visible');
          setLoading(false);
          return;
          
        } catch (error: any) {
          console.error('First-time login error:', error);
          
          // Make sure to reset showPasswordChange on error
          setShowPasswordChange(false);
          
          // Handle specific Firebase auth errors
          if (error.code === 'auth/user-not-found') {
            throw new Error('No account found with this email. Please contact your administrator.');
          } else if (error.code === 'auth/wrong-password') {
            throw new Error('Invalid temporary password. Please check and try again.');
          } else if (error.code === 'auth/too-many-requests') {
            throw new Error('Too many failed attempts. Please try again later.');
          } else if (error.code === 'permission-denied') {
            throw new Error('Access denied. Please contact your administrator.');
          }
          
          throw error;
        }
        
      } else {
        // Regular password login
        await AdminAuthService.login(email, password);
        
        // Clear any old admin data first
        localStorage.removeItem('adminData');
        localStorage.removeItem('adminEmail');
        localStorage.removeItem('adminDocId');
        localStorage.removeItem('adminRole');
        
        // CRITICAL: Check if user exists in adminUsers collection FIRST
        // Only users added by admin should be able to login
        const adminsRef = collection(db, 'adminUsers');
        const q = query(adminsRef, where('email', '==', email.trim()));
        const snapshot = await getDocs(q);
        
        // If user is not in adminUsers collection, they are NOT an admin
        if (snapshot.empty) {
          // Sign out the user immediately
          await AdminAuthService.logout();
          throw new Error('Access Denied: You are not authorized to access the admin dashboard. Only admin users can login here.');
        }
        
        // Extract name from email
        const emailName = email.split('@')[0].replace(/[._-]/g, ' ')
          .split(' ')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
        
        let adminInfo;
        let adminDocId = '';
        
        // Admin exists in adminUsers collection - proceed with login
        const adminDoc = snapshot.docs[0];
        const adminData = adminDoc.data();
        adminDocId = adminDoc.id;
        
        console.log('📊 Admin data from Firestore:', {
          email: adminData.email,
          passwordChanged: adminData.passwordChanged,
          status: adminData.status
        });
        
        // CRITICAL: Check if this is a new admin who hasn't set their password yet
        if (adminData.passwordChanged === false || !adminData.passwordChanged) {
          // Log them out immediately
          await AdminAuthService.logout();
          throw new Error('This account has not been activated yet. Please use "First Time Login" to set your password.');
        }
          
        console.log('📊 Firestore admin data retrieved:', {
          hasData: !!adminData,
          hasProfileImage: !!adminData.profileImage,
          profileImageUrl: adminData.profileImage,
          name: adminData.fullName || adminData.name,
          email: adminData.email,
          allKeys: Object.keys(adminData)
        });
        
        adminInfo = {
          email: adminData.email || email,
          fullName: adminData.fullName || adminData.name || emailName,
          role: adminData.role || 'Administrator',
          profileImage: adminData.profileImage || null // Include profile image
        };
        
        console.log('✅ Admin data with profile image:', {
          ...adminInfo,
          hasProfileImage: !!adminInfo.profileImage
        });
        
        console.log('📝 Setting admin data and session:', adminInfo);
        
        // Store admin data in localStorage  
        localStorage.setItem('adminEmail', email);
        localStorage.setItem('adminData', JSON.stringify(adminInfo));
        localStorage.setItem('adminDocId', adminDocId);
        localStorage.setItem('adminRole', adminInfo.role || 'Administrator');
        
        // Dispatch custom event to notify other components
        window.dispatchEvent(new Event('adminDataUpdated'));
        
        // Refresh authentication state
        refreshAuth();
        
        console.log('✅ Login successful, session established');
        
        // Call success callback if provided
        if (onLoginSuccess) {
          onLoginSuccess();
        }
        
        // Navigate to dashboard
        navigate('/dashboard', { replace: true });
      }
      
    } catch (error: any) {
      console.error('Login error:', error);
      setError(error.message || getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async () => {
    setError('');
    setNewPasswordError('');

    if (!newPassword || newPassword.length < 8) {
      setNewPasswordError('Password must be at least 8 characters');
      return;
    }

    if (newPassword !== confirmPassword) {
      setNewPasswordError('Passwords do not match');
      return;
    }

    setLoading(true);

    try {
      console.log('🔐 Starting password change process...');
      
      // At this point, the user should already be authenticated from the first-time login
      const currentUser = auth.currentUser;
      
      if (!currentUser) {
        throw new Error('Authentication session expired. Please try logging in again.');
      }
      
      console.log('Current user:', currentUser.email);
      console.log('Current user:', currentUser.email);
      
      // Update password in Firebase Authentication
      await updatePassword(currentUser, newPassword);
      console.log('✅ Firebase password updated');
      
      // Extract name from email if fullName not available
      const emailName = email.split('@')[0].replace(/[._-]/g, ' ')
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
      
      // Update admin record in Firestore with complete profile data
      const adminRef = doc(db, 'adminUsers', pendingAdminData.docId);
      await updateDoc(adminRef, {
        passwordChanged: true,
        status: 'active',
        lastPasswordChange: new Date().toISOString(),
        temporaryPassword: null, // Remove temporary password
        fullName: pendingAdminData.fullName || emailName, // Ensure fullName is set
        phoneNumber: pendingAdminData.phoneNumber || '+91-XXXXXXXXXX', // Ensure phone is set
        department: pendingAdminData.department || 'Administration' // Ensure department is set
      });
      
      console.log('✅ Admin record updated in Firestore');
      
      const adminInfo = {
        email: email,
        fullName: pendingAdminData.fullName || pendingAdminData.name || emailName,
        role: pendingAdminData.role || 'Administrator'
      };
      
      console.log('Setting admin data after password change:', adminInfo);
      
      // Store updated session and admin data
      localStorage.setItem('adminEmail', email);
      localStorage.setItem('adminData', JSON.stringify(adminInfo));
      localStorage.setItem('adminDocId', pendingAdminData.docId);
      localStorage.setItem('adminRole', pendingAdminData.role || 'Admin');
      localStorage.setItem('isAuthenticated', 'true');
      
      // Dispatch custom event to notify other components
      window.dispatchEvent(new Event('adminDataUpdated'));

      // Clear password setup mode flag
      sessionStorage.removeItem('passwordSetupMode');

      showNotification('Password updated successfully! Redirecting to dashboard...', 'success');
      
      console.log('✅ Password updated and admin account activated');
      
      // Refresh authentication state
      refreshAuth();

      setTimeout(() => {
        navigate('/dashboard', { replace: true });
        if (onLoginSuccess) {
          onLoginSuccess();
        }
      }, 1000);

    } catch (error: any) {
      console.error('Password update error:', error);
      
      if (error.code === 'auth/weak-password') {
        setNewPasswordError('Password is too weak. Please use a stronger password.');
      } else if (error.code === 'auth/requires-recent-login') {
        setError('For security, please log out and log in again, then change your password.');
      } else {
        setError(error.message || 'Failed to update password. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const showNotification = (message: string, severity: 'success' | 'error') => {
    // You can implement a snackbar or alert here
    console.log(message);
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  // Debug: Log render state
  console.log('🎨 Login component render - showPasswordChange:', showPasswordChange, 'loginMode:', loginMode);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.3)), url('/images/hospital-building.png')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        padding: 2
      }}
    >
      <Container maxWidth="sm">
        <Card elevation={10} sx={{ borderRadius: 2 }}>
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
              <Box
                component="img"
                src="/images/logo.png"
                alt="KBR Life Care Logo"
                sx={{ 
                  width: '80px', 
                  height: '80px', 
                  mb: 2,
                  objectFit: 'contain'
                }} 
              />
              <Typography 
                variant="h4" 
                component="h1" 
                gutterBottom 
                align="center"
                sx={{ 
                  fontWeight: 'bold',
                  color: 'primary.main',
                  mb: 0
                }}
              >
                KBR Life Care
              </Typography>
            </Box>
            
            <Typography 
              variant="h6" 
              component="h2" 
              gutterBottom 
              align="center"
              sx={{ mb: 3, color: 'text.secondary' }}
            >
              Admin Dashboard Login
            </Typography>

            <Tabs 
              value={loginMode} 
              onChange={(e, value) => {
                setLoginMode(value);
                setError('');
                setEmailError('');
                setPasswordError('');
                setNewPasswordError('');
              }}
              centered
              sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
            >
              <Tab 
                label="Password Login" 
                value="password"
                icon={<Lock />}
                iconPosition="start"
              />
              <Tab 
                label="First Time Login" 
                value="firstTime"
                icon={<VpnKey />}
                iconPosition="start"
              />
            </Tabs>

            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            {!showPasswordChange ? (
              <form onSubmit={handleSubmit}>
                <TextField
                  fullWidth
                  label="Admin Email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value.trim())}
                  margin="normal"
                  required
                  disabled={loading}
                  variant="outlined"
                  error={!!emailError}
                  helperText={emailError}
                  autoComplete="email"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Email sx={{ color: 'action.active' }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&:hover fieldset': {
                        borderColor: 'primary.main',
                      },
                    },
                  }}
                />
              
                <TextField
                  fullWidth
                  label={loginMode === 'password' ? 'Password' : 'Temporary Password'}
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  margin="normal"
                  required
                  disabled={loading}
                  variant="outlined"
                  error={!!passwordError}
                  helperText={passwordError || (loginMode === 'firstTime' ? 'Enter the temporary password provided by your administrator' : '')}
                  autoComplete="current-password"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock sx={{ color: 'action.active' }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={handleTogglePasswordVisibility}
                          onMouseDown={(e) => e.preventDefault()}
                          edge="end"
                          disabled={loading}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    mb: 3,
                    '& .MuiOutlinedInput-root': {
                      '&:hover fieldset': {
                        borderColor: 'primary.main',
                      },
                    },
                  }}
                />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={loading || !email || !password}
                sx={{ 
                  py: 1.5,
                  fontSize: '1rem',
                  fontWeight: 'bold',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  boxShadow: '0 4px 15px 0 rgba(102, 126, 234, 0.4)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #5a67d8 0%, #6b4c93 100%)',
                    boxShadow: '0 6px 20px 0 rgba(102, 126, 234, 0.6)',
                    transform: 'translateY(-2px)',
                  },
                  '&:disabled': {
                    background: '#e0e0e0',
                    color: '#9e9e9e',
                    boxShadow: 'none',
                    transform: 'none',
                  }
                }}
              >
                {loading ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={20} color="inherit" />
                    <Typography>Signing In...</Typography>
                  </Box>
                ) : (
                  loginMode === 'password' ? 'Sign In to Dashboard' : 'Continue to Set Password'
                )}
              </Button>
            </form>
            ) : (
              <Box>
                <Typography variant="h6" sx={{ mb: 2, textAlign: 'center', color: 'primary.main' }}>
                  Set Your New Password
                </Typography>
                <Typography variant="body2" sx={{ mb: 3, textAlign: 'center', color: 'text.secondary' }}>
                  Please create a secure password for your account
                </Typography>

                <TextField
                  fullWidth
                  label="New Password"
                  type={showPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  margin="normal"
                  required
                  disabled={loading}
                  variant="outlined"
                  error={!!newPasswordError}
                  helperText={newPasswordError || 'Password must be at least 8 characters'}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock sx={{ color: 'action.active' }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                          disabled={loading}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <TextField
                  fullWidth
                  label="Confirm Password"
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  margin="normal"
                  required
                  disabled={loading}
                  variant="outlined"
                  helperText="Re-enter your new password"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock sx={{ color: 'action.active' }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ mb: 3 }}
                />

                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  onClick={handlePasswordChange}
                  disabled={loading || !newPassword || !confirmPassword}
                  sx={{ 
                    py: 1.5,
                    fontSize: '1rem',
                    fontWeight: 'bold',
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  }}
                >
                  {loading ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CircularProgress size={20} color="inherit" />
                      <Typography>Updating Password...</Typography>
                    </Box>
                  ) : (
                    'Update Password & Login'
                  )}
                </Button>
              </Box>
            )}

            <Typography 
              variant="body2" 
              align="center" 
              sx={{ mt: 3, color: 'text.secondary' }}
            >
              {loginMode === 'password' 
                ? '🔒 Admin access only • Secure login required'
                : '🆕 First time? Use the temporary password provided by your administrator'
              }
            </Typography>

            <Typography 
              variant="body2" 
              align="center" 
              sx={{ 
                mt: 2, 
                display: 'block',
                color: 'text.primary',
                fontSize: '1rem',
                fontWeight: 'bold'
              }}
            >
              Developed by BRISTLETECH
            </Typography>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

export default Login;